import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { isEntityGroupPartnerRole, isOpenGovAdmin, userSelector } from '@opengov/app-core/session/selectors';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import {
  EDIT_STORY_RESOURCE,
  FLAG_PRODUCT_STORIES_ACCESS_CONTROL,
  FLAG_STORIES_CONTENT_BREAKS_AND_PADDING,
  FLAG_STORIES_PAGE_BREAK_EXPORTS,
} from '@opengov/stories-shared/constants';

import { storiesMsgs } from '../../locale';
import {
  selectAddPropsalsToStoriesFlag,
  selectStoriesPublicationFlag,
  selectUseAiToGenerateContentFlag,
  selectStoriesSummarizerGAFlag,
  selectStoriesOgAssistGenerateSummaryFlag,
  selectStoriesProgressTrackerFlag,
  selectIsAiToggleFeatureEnabled,
  selectIsCustomSpreadsheetReportEnabledFlag,
  selectIsFormAndWorkflowsFeatureFlagEnabled,
  selectIsPerformanceManagementFeatureFlagEnabled
} from '../common/flags';
import { IStoryViewProps } from '../components/StoryView';
import {
  navigateToStoryEdit,
  navigateToStoryList,
  navigateToStoryView,
} from '../state';
import {
  navigateToPublicationList,
  navigateToPublicationStoryEditPath,
  navigateToPublicationStorySetupPDF,
  navigateToPublicationView,
  navigateToStorySetupPDF
} from '../state/navigation';

import { selectIsTemplate, selectLastPath } from './storiesSelectors';
import { selectCopyIndividualStoriesFlag } from './storyListSelectors';

export const selectStoryId = createSelector(getLocation, (location) => location.params && location.params.storyId);

export const selectPublicationId = createSelector(
  getLocation,
  (location) => location.params?.publicationId ?? ''
);

export const selectStorySetupPdfFlag = createSelector(selectFeatureFlags, (flags) =>
  flags.get('product-feature-publications-setup-pdf')
);

export const selectNavigateToStoryProps = createStructuredSelector<any, any>({
  entitySubdomain: getEntitySubdomain,
  storyId: selectStoryId,
  isTemplate: selectIsTemplate
});

export const selectNavigateToPublicationViewProps = createStructuredSelector<any, any>({
  entitySubdomain: getEntitySubdomain,
  publicationId: selectPublicationId
});

export const getUserId = createSelector(userSelector, (user) => user.id);
export const getUserUuid = createSelector(userSelector, (user) => user.uuid);

export const selectStoryViewProps = createStructuredSelector<any, IStoryViewProps>({
  canAddProposalContent: selectAddPropsalsToStoriesFlag,
  isTemplate: selectIsTemplate,
  isEntityGroupPartner: isEntityGroupPartnerRole,
  isOpenGovAdmin,
  isEntityUserWithEdit: createSelector(selectFeatureFlags, (flags) => flags.get(EDIT_STORY_RESOURCE)),
  canCopyIndividualStories: selectCopyIndividualStoriesFlag,
  canEditSpacing: createSelector(selectFeatureFlags, (flags) => flags.get(FLAG_STORIES_CONTENT_BREAKS_AND_PADDING)),
  showProgressTracker: selectStoriesProgressTrackerFlag,
  canEditPageBreaks: createSelector(selectFeatureFlags, (flags) => flags.get(FLAG_STORIES_PAGE_BREAK_EXPORTS)),
  canUseAiToGenerateContent: selectUseAiToGenerateContentFlag,
  canUseStoriesSummarizerGA: selectStoriesSummarizerGAFlag,
  canUseOgAssistGenerateSummary: selectStoriesOgAssistGenerateSummaryFlag,
  isAiToggleFeatureEnabled: selectIsAiToggleFeatureEnabled,
  publicationId: selectPublicationId,
  lastPath: selectLastPath,
  storyId: selectStoryId,
  entityId: getNumericalEntityId,
  entityName: createSelector(getEntity, (entity) => entity.display_name),
  entitySubdomain: createSelector(getEntitySubdomain, (entitySubdomain) => entitySubdomain),
  entityUuid: createSelector(getEntity, (entity) => entity.uuid ?? ''),
  userId: getUserId,
  userUuid: getUserUuid,
  messages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.storyView })),
  editorMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.editor })),
  modalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.modals })),
  isCustomSpreadsheetReportEnabled: selectIsCustomSpreadsheetReportEnabledFlag,
  isFormsAndWorkflowsFeatureEnabled: selectIsFormAndWorkflowsFeatureFlagEnabled,
  isPerformanceManagementFeatureEnabled: selectIsPerformanceManagementFeatureFlagEnabled,
  openModal: () => (modal) => modalActionCreators.setModalComponentType(modal),
  displayPublicationTab: selectStoriesPublicationFlag,
  navigateToStoryList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => () => navigateToStoryList(entitySubdomain)
  ),
  navigateToStoryEdit: createSelector(
    selectNavigateToStoryProps,
    ({ entitySubdomain, storyId, isTemplate }: { entitySubdomain: string; storyId: string; isTemplate: boolean }) =>
      () =>
        navigateToStoryEdit(entitySubdomain, storyId, isTemplate)
  ),
  navigateToStoryView: createSelector(
    selectNavigateToStoryProps,
    ({ entitySubdomain, storyId, isTemplate }: { entitySubdomain: string; storyId: string; isTemplate: boolean }) =>
      () =>
        navigateToStoryView(entitySubdomain, storyId, isTemplate)
  ),
  navigateToPublicationView: createSelector(
    selectNavigateToPublicationViewProps,
    ({ entitySubdomain, publicationId }: { entitySubdomain: string; publicationId: string }) =>
      () =>
        navigateToPublicationView(entitySubdomain, publicationId)
  ),
  navigateToPublicationList: createSelector(
    selectNavigateToPublicationViewProps,
    ({ entitySubdomain }: { entitySubdomain: string }) =>
      () =>
        navigateToPublicationList(entitySubdomain)
  ),
  navigateToPublicationEdit: createSelector(
    selectNavigateToPublicationViewProps,
    ({ entitySubdomain }: { entitySubdomain: string }) =>
      (storyId, isTemplate, publicationId) =>
        navigateToPublicationStoryEditPath(entitySubdomain, storyId, isTemplate, publicationId)
  ),
  storiesAccessControlEnabled: createSelector(selectFeatureFlags, (flags) =>
    flags.get(FLAG_PRODUCT_STORIES_ACCESS_CONTROL)
  ),

  navigateToStorySetupPDF: createSelector(selectNavigateToStoryProps, ({ entitySubdomain, storyId }: { entitySubdomain: string, storyId: string }) =>
    () => navigateToStorySetupPDF(entitySubdomain, storyId)
  ),

  navigateToPublicationStorySetupPDF: createSelector(selectNavigateToStoryProps, ({ entitySubdomain }: { entitySubdomain: string }) =>
    (storyId: string, publicationId: string) => navigateToPublicationStorySetupPDF(entitySubdomain, storyId, publicationId)
  ),

  isStorySetupPdfEnabled: selectStorySetupPdfFlag,
});
